// External Google Form — the single intake / questionnaire surface for the site.
export const GOOGLE_FORM_URL = "https://forms.gle/vjsyJXXUWg2qh9EaA";

// Lead form used by the "Get Results Like This" case-study banners.
export const CASE_STUDY_FORM_URL = "https://forms.gle/RiytPah29KUCRadu9";

// Lead form for all Experience Blueprint™ CTAs site-wide.
export const EXPERIENCE_BLUEPRINT_FORM_URL = "https://forms.gle/mmZn6ZBpLyTaMicLA";

// Lead form for all AXIS Character Branding™ CTAs.
export const CHARACTER_BRANDING_FORM_URL = "https://forms.gle/SRFTf9aMK5t5856v8";

export const BRAND = {
  company: "AXIS Business Solutions",
  product: "AXIS Signal",
  tagline: "AI Optimization. Human Judgment.",
  quickLook: "AXIS Quick Look™",
};

// Services catalog (used in Featured Services, Pricing, Industry pages)
export const SERVICES = [
  {
    id: "experience-blueprint",
    name: "The Experience Blueprint™",
    price: "From B$1,500",
    short: "Flagship consulting engagement",
    desc: "Our signature on-site assessment that documents how customers experience your business — observation, analysis, documentation, visualization, recommendation, implementation.",
    includes: [
      "Professional on-site visit",
      "Customer journey documentation",
      "Identity, flow, atmosphere audit",
      "Opportunity scoring",
      "AXIS Concept Visualizations",
      "Vendor-coordinated implementation",
    ],
    accent: "from-[#D4AF37] to-[#1E3A8A]",
    featured: true,
    flagship: true,
    quoteOnly: true,
  },
  {
    id: "quick-look",
    name: "AXIS Quick Look™",
    price: "Free",
    short: "Discovery consultation",
    desc: "A discovery consultation to understand your business, challenges, and goals.",
    includes: [],
    accent: "from-[#1E3A8A] to-[#0A192F]",
    featured: false,
  },
  {
    id: "flow",
    name: "AXIS Flow™",
    price: "B$250",
    short: "Customer Flow Assessment",
    desc: "Improve how customers move through your business.",
    includes: [
      "Customer Journey Review",
      "WhatsApp Assessment",
      "Communication Audit",
      "Message Templates",
      "Follow-Up Recommendations",
      "Improvement Roadmap",
    ],
    accent: "from-[#1E3A8A] to-[#0A192F]",
    featured: true,
  },
  {
    id: "identity",
    name: "AXIS Identity™",
    price: "B$250",
    short: "Brand Direction Assessment",
    desc: "Understand how customers see your business.",
    includes: [
      "Brand Review",
      "Customer Experience Audit",
      "Identity Scorecard",
      "Asset Review",
      "Gap Analysis",
      "90-Day Roadmap",
    ],
    accent: "from-[#1E3A8A] to-[#0A192F]",
    featured: true,
  },
  {
    id: "flowdentity",
    name: "Flowdentity™",
    price: "B$400",
    short: "The Foundation Bundle",
    desc: "Complete business foundation assessment. Includes everything in Flow + Identity.",
    includes: ["Everything in AXIS Flow™", "Everything in AXIS Identity™", "Most popular starting point"],
    accent: "from-[#D4AF37] to-[#1E3A8A]",
    featured: true,
    popular: true,
  },
  {
    id: "keep-it-running",
    name: "Keep It Running™",
    price: "B$40/month",
    short: "Ongoing support & accountability",
    desc: "Monthly cadence to keep improvements moving and accountable.",
    includes: ["Monthly Check-In", "Progress Tracking", "Recommendations", "Priority Support"],
    accent: "from-[#1E3A8A] to-[#0A192F]",
    featured: false,
  },
];

// Industries catalog — reusable template renders these
export const INDUSTRIES = [
  {
    slug: "barbers",
    name: "Barbers",
    tagline: "Sharper chairs. Stronger retention.",
    image:
      "https://images.unsplash.com/photo-1567894340315-735d7c361db0?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Walk-ins out-pace booked appointments",
      "Loyal clients drift to other shops after small irritations",
      "WhatsApp inquiries get lost between chairs",
    ],
    journey: [
      "No clear booking confirmation flow",
      "First-time clients arrive without context",
      "No follow-up after first cut",
    ],
    brand: [
      "Identity inconsistent across signage, IG, and shopfront",
      "Premium service, average perception",
    ],
    flow:
      "Tighten WhatsApp booking, confirmations, and follow-ups so chairs stay full and retention climbs.",
    identity:
      "Align signage, social, and in-shop touchpoints so the brand reads as premium from the curb to the chair.",
    recommended: "flowdentity",
  },
  {
    slug: "hair-salons",
    name: "Hair Salons",
    tagline: "Consistent experience across every stylist.",
    image:
      "https://images.unsplash.com/photo-1613876214872-a73df2a1b8bc?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Experience varies by stylist",
      "Pricing structure confuses new clients",
      "Inconsistent rebooking",
    ],
    journey: [
      "Booking process feels manual and slow",
      "No structured consultation",
      "Aftercare advice depends on the stylist",
    ],
    brand: [
      "Brand voice inconsistent across platforms",
      "Visual identity not aligned with price point",
    ],
    flow:
      "Standardize consultation, booking, and rebooking flows so every chair delivers the same caliber of experience.",
    identity:
      "Refine identity, tone, and visual assets so the salon reads premium across every channel.",
    recommended: "flowdentity",
  },
  {
    slug: "nail-technicians",
    name: "Nail Technicians",
    tagline: "From DMs to durable client relationships.",
    image:
      "https://images.unsplash.com/photo-1610992015732-2449b76344bc?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Slot management lives in DMs",
      "No-shows eat into the day",
      "Clients shop by price, not relationship",
    ],
    journey: [
      "Booking via comments and DMs",
      "No deposit / confirmation system",
      "No structured follow-up",
    ],
    brand: [
      "Portfolio scattered across personal and business profiles",
      "Inconsistent before/after presentation",
    ],
    flow:
      "Move bookings out of DMs into a clean confirmation and reminder flow that protects the calendar.",
    identity:
      "Curate a focused portfolio and visual identity that earns premium pricing.",
    recommended: "flow",
  },
  {
    slug: "restaurants",
    name: "Restaurants",
    tagline: "Service that feels designed, not improvised.",
    image:
      "https://images.unsplash.com/photo-1687422808278-d17b09489ed1?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Front-of-house service inconsistency",
      "Online ordering and dine-in feel disconnected",
      "Reviews highlight the same issues repeatedly",
    ],
    journey: [
      "Reservation confirmations are unclear",
      "Wait times not communicated",
      "No structured feedback loop",
    ],
    brand: [
      "Menu, signage, and social media tell different stories",
      "Atmosphere does not match the price point",
    ],
    flow:
      "Diagnose service flow from reservation to follow-up so every guest experiences the same standard.",
    identity:
      "Align menu, signage, social, and atmosphere into one coherent brand.",
    recommended: "experience-blueprint",
  },
  {
    slug: "boutiques",
    name: "Boutiques",
    tagline: "Curated identity. Repeat customers.",
    image:
      "https://images.unsplash.com/photo-1601924994987-69e26d50dc26?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Foot traffic does not convert",
      "Inventory and merchandising lack story",
      "Social drives views but not visits",
    ],
    journey: [
      "Window draws attention but not entry",
      "In-store experience feels generic",
      "No follow-up after purchase",
    ],
    brand: [
      "Brand identity diluted across collections",
      "Voice does not match the customer profile",
    ],
    flow:
      "Tighten the path from window to checkout to follow-up so visits convert and return.",
    identity:
      "Refine the boutique's identity into a clear, ownable point of view.",
    recommended: "flowdentity",
  },
  {
    slug: "real-estate-agents",
    name: "Real Estate Agents",
    tagline: "Trust signals before the first showing.",
    image:
      "https://images.unsplash.com/photo-1655720357872-ce227e4164ba?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Leads cool down between touchpoints",
      "Inconsistent follow-up cadence",
      "Brand looks generic next to competitors",
    ],
    journey: [
      "First reply is slow and unstructured",
      "No clear next-step communication",
      "No post-close relationship management",
    ],
    brand: [
      "Listings and personal brand feel disconnected",
      "Visuals lack a recognizable identity",
    ],
    flow:
      "Build a follow-up engine that keeps leads warm and creates repeat referrals.",
    identity:
      "Position the agent as a credible, recognizable expert across every channel.",
    recommended: "flowdentity",
  },
  {
    slug: "hvac-plumbing",
    name: "HVAC & Plumbing",
    tagline: "Operational clarity for service businesses.",
    image:
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Jobs scheduled but not standardized",
      "Customer communication varies by technician",
      "No structured reporting on repeat issues",
    ],
    journey: [
      "Initial call inconsistent",
      "On-site experience varies",
      "No structured follow-up or maintenance reminder",
    ],
    brand: [
      "Truck, uniform, and digital presence not aligned",
      "Reviews undersell the actual quality of work",
    ],
    flow:
      "Standardize the customer-facing flow from first call to post-service review.",
    identity:
      "Build a recognizable, trusted local brand that customers refer.",
    recommended: "experience-blueprint",
  },
  {
    slug: "photographers",
    name: "Photographers",
    tagline: "Pricing, packaging, and presence — aligned.",
    image:
      "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Inquiries don't convert to bookings",
      "Pricing conversations feel awkward",
      "Portfolio strong, business operations weak",
    ],
    journey: [
      "Discovery happens, then silence",
      "No structured proposal or follow-up",
      "Delivery feels different every time",
    ],
    brand: [
      "Portfolio sprawls across styles",
      "Brand does not signal price point",
    ],
    flow:
      "Tighten inquiry → proposal → delivery so the business runs as well as the camera.",
    identity:
      "Sharpen positioning so pricing feels obvious, not awkward.",
    recommended: "flowdentity",
  },
  {
    slug: "car-rentals",
    name: "Car Rentals",
    tagline: "Fleet experience that earns repeat rentals.",
    image:
      "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1200&q=80",
    challenges: [
      "Booking and handover feel manual",
      "Customer expectations and reality diverge",
      "Repeat rentals lower than they should be",
    ],
    journey: [
      "Booking confirmation lacks clarity",
      "Pickup and return inconsistent",
      "No structured feedback loop",
    ],
    brand: [
      "Fleet presented without a coherent identity",
      "Customer-facing materials feel ad-hoc",
    ],
    flow:
      "Diagnose booking, handover, and return so customers come back instead of comparing prices.",
    identity:
      "Present the fleet as one professional brand, not a collection of vehicles.",
    recommended: "experience-blueprint",
  },
];

export const getServiceById = (id) => SERVICES.find((s) => s.id === id);
export const getIndustryBySlug = (slug) => INDUSTRIES.find((i) => i.slug === slug);
